import React, { FunctionComponent, useEffect, useRef } from 'react';
import clsx from 'clsx';
import CheckIcon from '@material-ui/icons/Check';
import { Button, useMediaQuery, useTheme } from '@material-ui/core';
import navigationElementStyles from './navigationElementStyles';
import { CustomiseSections } from '../../../../common/enum/CustomiseSections';
import { useTrimLevel } from '../../../../trimLevels/contexts/TrimLevelContext';
import { ISpcTheme } from '../../../../common/theme/ISpcTheme';
import useNavigationLabel from '../hooks/useNavigationLabel';
import { ScrollInViewBehaviourType } from '../../../../common/service/scrollInView';

export interface INavigationElementProps {
  onClick: (name: string) => void;
  completed: boolean;
  activeItem: string;
  disabledItem?: boolean;
  name: string;
  index: number;
  withTranslation: boolean;
  label?: string;
  testId?: string;
  hideItemNumber: boolean;
  isGraphql?: boolean;
  trimLabel?: string;
}

const NavigationElement: FunctionComponent<INavigationElementProps> = ({
  onClick,
  completed,
  activeItem,
  index,
  name,
  disabledItem,
  withTranslation,
  label,
  testId,
  hideItemNumber,
  isGraphql,
  trimLabel,
}) => {
  const styles = navigationElementStyles();
  const navigationItemRef = useRef<HTMLButtonElement | null>(null);
  const { modelName } = useTrimLevel();
  const {
    breakpoints,
    spc: {
      customise: { customNavigationElementStyle },
    },
  } = useTheme<ISpcTheme>();
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const applyCustomiseNavElementStyle = customNavigationElementStyle && !trimLabel;

  const { renderLabel } = useNavigationLabel(isGraphql, withTranslation);

  useEffect(() => {
    if (isMobile && activeItem === name) {
      navigationItemRef.current?.scrollIntoView({
        behavior: ScrollInViewBehaviourType.SMOOTH,
        block: 'start',
        inline: 'center',
      });
    }
  }, [activeItem, name, navigationItemRef, isMobile]);

  return (
    <Button
      ref={navigationItemRef}
      data-test-selected={activeItem === name}
      data-test={`common:navBarButton:${testId}`}
      classes={{
        label: clsx([
          styles.label,
          { [styles.modelNameLabel]: modelName, [styles.customiseLabel]: applyCustomiseNavElementStyle },
        ]),
      }}
      onClick={() => !disabledItem && onClick(name)}
      className={clsx([
        styles.item,
        {
          [styles.activeItem]: activeItem === name,
          [styles.disabledItem]: disabledItem,
          [styles.deselectedItem]: activeItem !== name,
          [styles.completedItem]: completed,
          [styles.customiseItem]: applyCustomiseNavElementStyle,
          [styles.customiseActiveItem]: activeItem === name && applyCustomiseNavElementStyle,
        },
      ])}
      disableRipple={!applyCustomiseNavElementStyle}
    >
      <CheckIcon className={clsx([styles.completedIcon, completed && styles.completedIconVisible])} />
      {name !== CustomiseSections.STOCK && !hideItemNumber && (
        <span data-test={`navBarButton:${testId}:itemNumber`} className={styles.itemNumber}>
          {index + 1}.
        </span>
      )}
      {renderLabel(label, name, trimLabel, styles.modelName)}
    </Button>
  );
};

export default NavigationElement;
